import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  JobList,
  Quote,
  StatBand,
  CTASection,
  Reveal,
} from './kit.jsx'

const PERKS = [
  {
    icon: 'globe',
    title: 'Remote-first, always',
    body: 'We hire across North American time zones and ship async. No mandatory office, no return-to-desk memo — your calendar and your keyboard are yours.',
    tag: 'Anywhere',
  },
  {
    icon: 'sparkle',
    title: 'Real equity, real early',
    body: 'Every full-time hire gets meaningful stock options with a standard 4-year vest. You are building the company, so you own a piece of it.',
  },
  {
    icon: 'shield',
    title: 'Health, covered',
    body: 'Company-paid medical, dental, and vision for you and your dependents, plus a monthly wellness stipend. We keep the plan good and the paperwork short.',
  },
  {
    icon: 'bolt',
    title: 'Build-fast culture',
    body: 'Small team, short meetings, tight feedback loops. Ideas go from Slack thread to shipped feature in days — not committees and quarterly planning cycles.',
  },
  {
    icon: 'hammer',
    title: 'Customers who build things',
    body: 'You talk to customers every week. The feedback is blunt, the problems are real, and the work you ship moves their revenue.',
  },
  {
    icon: 'clock',
    title: 'Flexible, sane hours',
    body: 'Unlimited PTO with a 3-week minimum we actually enforce, plus the whole last week of the year off. Do the best work of your career without burning out.',
  },
]

const CULTURE_STATS = [
  { value: '10,942', label: 'Verified leads our product serves' },
  { value: '100%', label: 'Remote-first since day one' },
  { value: '3 wks', label: 'Minimum PTO, enforced' },
  { value: '4 yr', label: 'Equity vest for every hire' },
]

const JOB_GROUPS = [
  {
    team: 'Engineering',
    roles: [
      { title: 'Senior Full-Stack Engineer (React + Node)', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
      { title: 'Backend Engineer, Data & Lead Pipeline', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
      { title: 'Infrastructure Engineer (AWS, Lambda, DynamoDB)', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
    ],
  },
  {
    team: 'Sales',
    roles: [
      { title: 'Account Executive, New Business', location: 'Remote · US', type: 'Full-time', to: '/contact' },
      { title: 'Sales Development Representative', location: 'Remote · US', type: 'Full-time', to: '/contact' },
      { title: 'Head of Sales', location: 'Remote · US', type: 'Full-time', to: '/contact' },
    ],
  },
  {
    team: 'Operations',
    roles: [
      { title: 'Customer Success Manager', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
      { title: 'Data Operations Analyst', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
      { title: 'Founding People & Talent Lead', location: 'Remote · US / Canada', type: 'Full-time', to: '/contact' },
    ],
  },
]

export default function CareersPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Careers"
        description="Join Revailo Sales and build the lead engine for B2B sales teams. Remote-first, real equity, and customers who build things every day."
        path="/careers"
        keywords="Revailo Sales careers, B2B software jobs, remote engineering jobs, sales jobs, sales tech careers"
      />

      <PageHero
        eyebrow="Careers"
        title={
          <>
            Build the tool that puts sales teams <br className="max-sm:hidden" />
            back in the driver&apos;s seat.
          </>
        }
        lead="We are a small, remote-first team turning 10,942 verified companies into a lead engine that B2B teams actually win with. If you like real customers, short feedback loops, and shipping fast, we should talk."
        primary={{ label: 'See open roles', to: '/contact' }}
        secondary={{ label: 'Talk to us', to: '/contact' }}
      />

      <Section id="perks">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Why work here"
              title={<>Serious work, without the corporate drag.</>}
              lead="We keep the team small, the ownership real, and the mission concrete: help sales teams find and win the deals already in their market."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={PERKS} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={CULTURE_STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="roles">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Open roles"
              title="Where we're hiring"
              lead="Nine open roles across Engineering, Sales, and Operations. Don't see an exact match? Reach out anyway — we hire for judgment, not job titles."
            />
          </Reveal>
          <div className="mt-14" style={{ maxWidth: 820, margin: '56px auto 0' }}>
            <Reveal>
              <JobList groups={JOB_GROUPS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <Reveal>
              <Quote
                quote="I've shipped at big companies where a feature took two quarters and three sign-offs. Here I watched a customer ask for saved filters on a Tuesday and use them the next Monday. That loop is why I stay."
                name="Priya Nair"
                role="Senior Engineer, joined 2025"
              />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Join us"
        title="Like the sound of it? Let's talk."
        body="Send us a note with what you'd want to work on and a link to something you're proud of. Every message reaches a founder — no black-hole application portal."
        primary={{ label: 'Reach out', to: '/contact' }}
        secondary={{ label: 'Learn about us', to: '/about' }}
      />
    </MarketingShell>
  )
}
